import * as React from 'react'
import { X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import { cn } from '@shared/ui/lib/utils'
import './drawer.css'

type DrawerSide = 'bottom' | 'left' | 'right' | 'top'

const Drawer = DialogPrimitive.Root
const DrawerTrigger = DialogPrimitive.Trigger
const DrawerPortal = DialogPrimitive.Portal
const DrawerClose = DialogPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DrawerOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      className={cn('fixed inset-0 z-50', className)}
      data-slot="drawer-overlay"
      ref={ref}
      {...props}
    />
  )
})

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  readonly hideClose?: boolean
  readonly side?: DrawerSide
}

const drawerSideClassNames: Record<DrawerSide, string> = {
  bottom: 'inset-x-0 bottom-0 max-h-[calc(100dvh-1rem)] rounded-t-lg border-t',
  left: 'inset-y-0 left-0 h-full w-[min(24rem,calc(100vw-2rem))] border-r',
  right: 'inset-y-0 right-0 h-full w-[min(24rem,calc(100vw-2rem))] border-l',
  top: 'inset-x-0 top-0 max-h-[calc(100dvh-1rem)] rounded-b-lg border-b',
}

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(function DrawerContent({
  children,
  className,
  hideClose = false,
  side = 'right',
  ...props
}, ref) {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed z-50 grid gap-4 overflow-y-auto p-6 shadow-lg outline-none',
          drawerSideClassNames[side],
          className,
        )}
        data-side={side}
        data-slot="drawer-content"
        ref={ref}
        {...props}
      >
        {children}
        {hideClose ? null : (
          <DialogPrimitive.Close
            className="absolute right-3 top-3 inline-flex size-10 items-center justify-center rounded-full outline-none transition-colors focus-visible:ring-[3px]"
            data-slot="drawer-close"
          >
            <X aria-hidden="true" className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DrawerPortal>
  )
})

function DrawerHeader({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn('grid gap-1.5 text-left', className)}
      data-slot="drawer-header"
      {...props}
    />
  )
}

function DrawerFooter({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn('mt-auto flex flex-col gap-2 sm:flex-row sm:items-center', className)}
      data-slot="drawer-footer"
      {...props}
    />
  )
}

const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DrawerTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      className={cn('m-0 text-lg font-bold leading-tight', className)}
      data-slot="drawer-title"
      ref={ref}
      {...props}
    />
  )
})

const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DrawerDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      className={cn('m-0 text-sm leading-6', className)}
      data-slot="drawer-description"
      ref={ref}
      {...props}
    />
  )
})

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
}
export type { DrawerContentProps, DrawerSide }
